import { AxiosRequestConfig, CancelTokenSource, Method } from 'axios';
import { SILENT_ERROR } from '../../../_util/types';
import { withRetries } from '../../../_util/withRetries';
import { isResponseOk } from './isResponseOk';
import { parseApiResponse } from './parseApiResponse';
import { removeRequestInFlight } from './requestsInFlight/index';
import { yemboAxiosInstance } from './yemboAxiosInstance';

type RequestDataType = Record<string, unknown> | undefined;

type Props = Pick<AxiosRequestConfig<RequestDataType>, 'method' | 'url' | 'data' | 'headers' | 'timeout'> & {
  isShortPoll?: boolean;
  didSucceed: (data: unknown) => boolean;
  onSuccess?: (parsedApiResponse: unknown) => unknown;
  cancelSource: CancelTokenSource;
  id?: string;
  method: Method;
  preventLogout?: boolean;
  signal?: AbortSignal;
};

export const makeRequest = async (props: Props) => {
  const {
    method,
    url,
    data,
    headers = {},
    timeout = 60000,
    isShortPoll,
    didSucceed,
    onSuccess,
    cancelSource,
    preventLogout = false,
    signal,
  } = props;

  const cancelToken = cancelSource.token;
  const requestParams = {
    method,
    data,
    url,
    headers,
    timeout,
    cancelToken,
    signal,
  };
  const apiRequest = () => yemboAxiosInstance<RequestDataType>(requestParams);
  const apiResponse = await withRetries(apiRequest, preventLogout);

  removeRequestInFlight(props as unknown as Parameters<typeof removeRequestInFlight>[0]);

  const parsedApiResponse = parseApiResponse({
    method,
    data,
    apiResponse,
  }) as unknown;
  const isShortPolling = isShortPoll && !didSucceed(parsedApiResponse);

  if (isShortPolling) {
    throw new Error(SILENT_ERROR);
  } else if (isResponseOk(apiResponse.status)) {
    return onSuccess?.(parsedApiResponse) ?? parsedApiResponse;
  } else {
    throw new Error(apiResponse.statusText);
  }
};
